import { RoleCode } from 'hooks/useGetRoles';
import { UserDetails } from 'hooks/useGetUserDetail';
import { create } from 'zustand';
import { persist, devtools } from 'zustand/middleware';

type NullableValuesObject<T extends object> = {
  [K in keyof T]: T[K] | null;
};

export type UserStoreType = NullableValuesObject<
  Omit<UserDetails, 'stores'>
> & {
  stores: UserDetails['stores'];
  actions: {
    setUserDetail: (data: UserDetails) => void;
    clearUserDetail: () => void;
    hasUserPermissionRole: (role: RoleCode) => boolean;
  };
};

const USER_STORE_NAME = 'user-details-store';

const initialUserStore: Omit<UserStoreType, 'actions'> = {
  id: null,
  email: null,
  role_id: null,
  stores: [],
  role_code: null,
  activated: null,
  created_at: null,
  is_active: null,
  name: null,
  phone_number: null,
  updated_at: null,
  franchise_owner_id: null,
};

export const useUserStore = create<UserStoreType>()(
  devtools(
    persist(
      (set, get) => ({
        ...initialUserStore,
        actions: {
          setUserDetail: (data) => {
            set({
              ...data,
            });
          },
          clearUserDetail: () => {
            set({
              ...initialUserStore,
            });
          },
          hasUserPermissionRole: (role) => {
            const { id, role_code: userRole } = get();

            // we can assume that user exists because this function is called only when user is logged in
            // lack of user is considered as a bug in the code
            if (!id) throw new Error('User does not exist');

            return userRole === role;
          },
        },
      }),
      {
        name: USER_STORE_NAME,
        partialize: (state) => ({
          id: state.id,
          email: state.email,
          role_id: state.role_id,
          stores: state.stores,
        }),
      }
    ),
    {
      name: USER_STORE_NAME,
    }
  )
);

export const useUserStoreActions = () => useUserStore((state) => state.actions);
